import { Fragment } from 'react';
import { MdLock } from 'react-icons/md';
import { CohortLockState, getActionStepLabel } from '../utils';

interface LockBannerProps {
  lockState: CohortLockState;
}

/**
 * Banner shown at the top of a step popup when the cohort is locked.
 * - `scheduled` with 1 triggering action: tells the user to cancel that step.
 * - `scheduled` with multiple: lists every step so the user knows cancelling
 *   just one won't unlock - every scheduled send must be cancelled.
 * - `sent`: permanent lock; names the most recent sent step for context.
 */
const LockBanner = ({ lockState }: LockBannerProps) => {
  if (!lockState.locked) return null;

  const isScheduled = lockState.reason === 'scheduled';
  const actions = lockState.triggeringActions;
  if (actions.length === 0) return null;

  const stepNames = actions.map((a) => getActionStepLabel(a.action));
  const primaryStep = stepNames[0];
  const multiple = isScheduled && actions.length > 1;

  const bgClass = isScheduled
    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
    : 'bg-gray-100 dark:bg-gray-900/40 border-gray-300 dark:border-gray-700';

  const iconColor = isScheduled
    ? 'text-amber-600 dark:text-amber-400'
    : 'text-gray-600 dark:text-gray-300';

  const title = isScheduled ? 'Edits locked - send scheduled' : 'Edits locked - emails sent';

  // Renders a list like "A, B and C" with <strong> around each name.
  const renderStepList = (names: string[]) =>
    names.map((name, i) => (
      <Fragment key={`${name}-${i}`}>
        {i > 0 && (i === names.length - 1 ? ' and ' : ', ')}
        <strong>{name}</strong>
      </Fragment>
    ));

  return (
    <div className={`mb-4 p-4 rounded-lg border ${bgClass}`}>
      <div className="flex items-start gap-2">
        <MdLock className={`h-5 w-5 flex-shrink-0 mt-0.5 ${iconColor}`} />
        <div className="flex-1">
          <h4 className="font-semibold text-navy-700 dark:text-white mb-1">{title}</h4>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {isScheduled ? (
              multiple ? (
                <>
                  Multiple sends are scheduled. Cancel them in the {renderStepList(stepNames)}{' '}
                  steps to unlock edits.
                </>
              ) : (
                <>
                  Participants and matches can't be changed while a send is scheduled. Cancel the
                  scheduled send in the <strong>{primaryStep}</strong> step to unlock edits.
                </>
              )
            ) : (
              <>
                Emails have already been sent for the <strong>{primaryStep}</strong> step.
                Participants and matches for this cohort can no longer be changed.
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LockBanner;
